import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

interface Point {
  x: number;
  y: number;
}

interface Props {
  data: any[];
  yField: string; // ex: "fitted_trend_abs_top_blended_presence_rate"
  maxCpcValue?: number | string | null;
}

const CustomLineChart: React.FC<Props> = ({ data, yField, maxCpcValue }) => {
  //---------------------------------------------------------------------------
  // TRANSFORMATION : scatter + ligne (moyenne par max CPC)
  //---------------------------------------------------------------------------
  const { scatter, line } = useMemo(() => {
    const scatter: Point[] = (data ?? [])
      .filter((p) => p.max_cpc != null && p[yField] != null)
      .map((p) => ({
        x: Number(p.max_cpc),
        y: Number(p[yField]),
      }));

    // Regrouper par x pour faire une VRAIE courbe lissée
    const grouped: Record<number, number[]> = {};
    scatter.forEach(({ x, y }) => {
      const key = +x.toFixed(3);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(y);
    });

    const line: Point[] = Object.entries(grouped)
      .map(([x, values]) => ({
        x: Number(x),
        y: values.reduce((a, b) => a + b, 0) / values.length,
      }))
      .sort((a, b) => a.x - b.x);

    return { scatter, line };
  }, [data, yField]);

  //---------------------------------------------------------------------------
  // CALCUL DU PADDING DES AXES (évite collisions avec les borders)
  //---------------------------------------------------------------------------
  const xVals = scatter.map((p) => p.x);
  const yVals = scatter.map((p) => p.y);

  const xMin = Math.min(...xVals);
  const xMax = Math.max(...xVals);
  const yMin = Math.min(...yVals);
  const yMax = Math.max(...yVals);

  const xPadding = (xMax - xMin) * 0.06; // 4% padding horizontal
  const yPadding = (yMax - yMin) * 0.10; // 6% padding vertical

  //---------------------------------------------------------------------------
  // OPTIONS APEX
  //---------------------------------------------------------------------------
  const options: ApexOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
      animations: { enabled: false },
      background: "transparent",
      zoom: { enabled: false },
    },

    grid: {
      show: false,
    },

    legend: { show: false },

    colors: ["#3C454F", "#FFC940"], // points puis ligne

    stroke: {
      width: [0, 2.5],
      curve: "smooth",
    },

    markers: {
      size: [5, 0],
      colors: ["#3C454F"],
      strokeColors: "#0D1218",
      strokeWidth: 1.2,
    },

    //---------------------------------------------------------------------------
    // AXE X (Max CPC)
    //---------------------------------------------------------------------------
    xaxis: {
      type: "numeric",
      min: xMin - xPadding,
      max: xMax + xPadding,
      labels: {
        formatter: (val) => Number(val).toFixed(2),
        style: {
          colors: "#BFCAD6",
          fontSize: "11px",
        },
      },
      axisBorder: {
        show: true,
        color: "rgba(255,255,255,0.25)",
      },
      axisTicks: { show: false },
      title: {
        text: "Max CPC",
        style: {
          color: "#7A8896",
          fontSize: "12px",
          fontWeight: 600,
        },
      },
    },

    //---------------------------------------------------------------------------
    // AXE Y (KPI : 0–100 %)
    //---------------------------------------------------------------------------
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        show: false,
      },
      axisBorder: {
        show: true,
        color: "rgba(255,255,255,0.25)",
      },
      axisTicks: { show: false },
    },

    //---------------------------------------------------------------------------
    // MARQUEUR MAX CPC (ligne verticale + étiquette)
    //---------------------------------------------------------------------------
    annotations:
      maxCpcValue !== null && maxCpcValue !== undefined
        ? {
            xaxis: [
              {
                x: Number(maxCpcValue),
                borderColor: "#00B4FF",
                strokeDashArray: 0,
                opacity: 1,
                label: {
                  text: Number(maxCpcValue).toFixed(2),
                  offsetY: -10,
                  style: {
                    background: "#00B4FF",
                    color: "#fff",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: {
                      left: 6,
                      right: 6,
                      top: 2,
                      bottom: 2,
                    },
                  },
                },
              },
            ],
          }
        : undefined,

    tooltip: { enabled: false },
  };

  //---------------------------------------------------------------------------
  // SERIES
  //---------------------------------------------------------------------------
  const series = [
    { name: "Scatter", type: "scatter" as const, data: scatter },
    { name: "Line", type: "line" as const, data: line },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Chart options={options} series={series} type="line" height={230} />
    </div>
  );
};

export default CustomLineChart;
